import * as React from "react";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MINI_STAT_COUNT_GRADIENT, MINI_STAT_OPEN_GRADIENT } from "@/utils/miniStatGradient";
import { useBillingContext } from "../../contexts/BillingContext";
import { setBillingAddon, startCreditPackCheckout } from "../../services/billingApi";
import {
  liveCardBodyClassName,
  liveCardClassName,
  liveCardTitleClassName,
  liveInsetClassName,
  liveOptionTitleClassName,
  liveSmallTextClassName,
} from "../../views/Dashboard/LiveStrategies/liveStrategiesUi";
import { LiveBadge } from "../../views/Dashboard/LiveStrategies/LiveUiParts";
import TradeLogMiniStatistics from "../../views/Dashboard/TradeLog/components/TradeLogMiniStatistics";

const fmt = (n: number | null | undefined) => (n == null ? "–" : n.toLocaleString());

/**
 * TES credits on the Billing tab: what's left this month (or this week's prompts on Free),
 * top-up packs with this user's bonus, and live / paper slot add-ons. Amounts come from
 * GET /billing/status; Stripe charges them. Built from the Launchpad card kit (liveStrategiesUi).
 */
export default function AiCreditsCard() {
  const { status, isTier1Granted, refresh } = useBillingContext();
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const ai = status?.ai;
  const packs = status?.packs;
  const ent = status?.entitlements;
  const bt = status?.backtests;

  const buy = async (packId: string) => {
    setBusy(packId);
    setError(null);
    try {
      const { url } = await startCreditPackCheckout(packId);
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't open checkout.");
      setBusy(null);
    }
  };

  const addSlot = async (addon: "live_slot" | "paper_slots", delta = 1) => {
    setBusy(addon);
    setError(null);
    try {
      await setBillingAddon(addon, Math.max(0, Number(status?.addons?.[addon] ?? 0) + delta));
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't update the add-on.");
    } finally {
      setBusy(null);
    }
  };

  if (!ai && !ent) return null;

  const prompts = ai?.mode === "prompts";
  const liveSlots = Number(status?.addons?.live_slot ?? 0);
  const paperSlots = Number(status?.addons?.paper_slots ?? 0);
  const left = prompts ? ai?.prompts_remaining : ai?.total_remaining;
  const of = prompts ? ai?.prompts_per_week : ai?.plan_credits;

  return (
    // data-live-ui: the Launchpad kit's scope, so the Chakra button / border reset doesn't strip padding.
    <div className={cn(liveCardClassName, liveCardBodyClassName, "w-full")} data-ai-credits-card="" data-live-ui="">
      <h3 className={liveCardTitleClassName}>TES & backtests</h3>

      <div data-mini-stats-grid="" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <TradeLogMiniStatistics
          title={prompts ? "TES prompts left" : "AI credits left"}
          amount={fmt(left)}
          secondaryAmount={`of ${fmt(of)} this ${prompts ? "week" : "month"}`}
          percentage={null}
          showPercentage={false}
          backgroundGradient={(left ?? 0) > 0 ? MINI_STAT_COUNT_GRADIENT : MINI_STAT_OPEN_GRADIENT}
          tooltip={prompts ? "Free includes 5 TES prompts a week." : "A typical prompt uses 1–5 credits; plan credits refill each month."}
          fitAmount
        />
        <TradeLogMiniStatistics
          title="Backtests left"
          amount={fmt(bt?.remaining)}
          secondaryAmount={`of ${fmt(bt?.cap)} this ${bt?.window === "month" ? "month" : "week"}`}
          percentage={null}
          showPercentage={false}
          backgroundGradient={(bt?.remaining ?? 0) > 0 ? MINI_STAT_COUNT_GRADIENT : MINI_STAT_OPEN_GRADIENT}
          tooltip="Every backtest, search or markets test you or TES run counts as one."
          fitAmount
        />
        {!prompts ? (
          <TradeLogMiniStatistics
            title="Bought credits"
            amount={fmt(ai?.packs_remaining)}
            secondaryAmount="valid 12 months"
            percentage={null}
            showPercentage={false}
            backgroundGradient={MINI_STAT_COUNT_GRADIENT}
            tooltip="Used after your plan's credits, soonest expiry first."
            fitAmount
          />
        ) : null}
        {ent?.paid ? (
          <TradeLogMiniStatistics
            title="Launchpad slots"
            amount={`${fmt(ent.deploy_live_max)} live`}
            secondaryAmount={`${fmt(ent.deploy_running_max)} running in total`}
            percentage={null}
            showPercentage={false}
            backgroundGradient={MINI_STAT_COUNT_GRADIENT}
            tooltip="Strategies on a broker account, and all strategies running at once (paper + live)."
            fitAmount
          />
        ) : null}
      </div>

      {isTier1Granted && packs?.available ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className={liveOptionTitleClassName}>Top up AI credits</span>
            {packs.bonus_pct ? (
              <span className={liveSmallTextClassName}>
                +{packs.bonus_pct}% bonus credits{packs.first_pack ? " on your first pack" : " on every pack"}
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {packs.packs.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={busy != null}
                onClick={() => void buy(p.id)}
                data-credit-pack={p.id}
                className={cn(
                  liveInsetClassName,
                  "group h-full items-start gap-3 bg-card p-4 text-left transition-colors dark:bg-sidebar",
                  "hover:!border-[color:var(--brand-tropical-surf)]/60 focus-visible:outline-none",
                  "focus-visible:ring-1 focus-visible:ring-[color:var(--brand-tropical-surf)] disabled:cursor-wait disabled:opacity-60"
                )}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="font-[family-name:var(--font-primary)] text-2xl font-medium leading-none text-[color:var(--brand-tropical-surf)]">
                    ${p.price_usd}
                  </span>
                  {packs.bonus_pct ? (
                    <LiveBadge tone="brand" data-pack-bonus="">
                      +{packs.bonus_pct}%
                    </LiveBadge>
                  ) : null}
                </span>
                <span className="flex flex-col gap-1">
                  <span className="flex items-baseline gap-1.5">
                    <span className="font-ibm-plex text-xl font-semibold tabular-nums text-gray-700 dark:text-white">
                      {fmt(p.credits)}
                    </span>
                    <span className={liveSmallTextClassName}>credits</span>
                  </span>
                  {p.credits > p.base_credits ? (
                    <span className={liveSmallTextClassName}>
                      <span className="line-through decoration-gray-400">{fmt(p.base_credits)}</span> without the bonus
                    </span>
                  ) : null}
                </span>
                <span className="mt-auto inline-flex items-center gap-1 font-[family-name:var(--font-primary)] text-xs font-medium text-[color:var(--brand-tropical-surf)]">
                  {busy === p.id ? "Opening checkout…" : "Buy pack"}
                  {busy === p.id ? null : (
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
                  )}
                </span>
              </button>
            ))}
          </div>
          <p className={cn(liveSmallTextClassName, "m-0")}>
            Bought credits last 12 months and are used after your plan's credits.
          </p>
        </div>
      ) : !isTier1Granted ? (
        <p className={cn(liveSmallTextClassName, "m-0")}>Upgrade for monthly AI credits and top-ups.</p>
      ) : null}

      {isTier1Granted && ent ? (
        <div className="flex flex-col gap-3">
          <span className={liveOptionTitleClassName}>Add Launchpad slots</span>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline-brand" size="sm" data-font="primary" className="shadow-none"
              disabled={busy != null} onClick={() => void addSlot("live_slot")} data-addon="live_slot">
              <span className="normal-case tracking-normal">+1 live broker slot · $9/month</span>
            </Button>
            <Button type="button" variant="outline-brand" size="sm" data-font="primary" className="shadow-none"
              disabled={busy != null} onClick={() => void addSlot("paper_slots")} data-addon="paper_slots">
              <span className="normal-case tracking-normal">+5 paper strategies · $5/month</span>
            </Button>
          </div>
          {liveSlots || paperSlots ? (
            <div className="flex flex-wrap items-center gap-2" data-addons-current="">
              <span className={liveSmallTextClassName}>On your plan:</span>
              {liveSlots ? (
                <Button type="button" variant="ghost" size="sm" className="shadow-none"
                  disabled={busy != null} onClick={() => void addSlot("live_slot", -1)} data-addon-remove="live_slot">
                  <span className="normal-case tracking-normal">
                    {liveSlots} extra live slot{liveSlots > 1 ? "s" : ""} · remove one
                  </span>
                </Button>
              ) : null}
              {paperSlots ? (
                <Button type="button" variant="ghost" size="sm" className="shadow-none"
                  disabled={busy != null} onClick={() => void addSlot("paper_slots", -1)} data-addon-remove="paper_slots">
                  <span className="normal-case tracking-normal">{paperSlots * 5} extra paper strategies · remove 5</span>
                </Button>
              ) : null}
            </div>
          ) : null}
          <p className={cn(liveSmallTextClassName, "m-0")}>
            Added to your subscription and prorated; removing one credits the unused time.
          </p>
        </div>
      ) : null}

      {error ? <p className="m-0 text-sm text-red-400">{error}</p> : null}
    </div>
  );
}
